import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { useI18n } from '../i18n/I18nContext';
import { AppHeader } from '../components/AppHeader';
import { useSmartBack } from '../hooks/useSmartBack';
import { setCoinBalance } from '../components/CoinChip';
import './ReferralPage.css';

const API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface ReferralMe {
  code: string;
  total_invited: number;
  total_active: number;
  total_earned: number;
  was_referred: boolean;
  signup_bonus: number;
  active_bonus_referrer: number;
  activation_wins_required: number;
}

interface InvitedItem {
  id: number;
  nickname: string;
  status: string;
  bonus_awarded: boolean;
  coins_earned: number;
  created_at: string | null;
  activated_at: string | null;
}

function fmtTpl(s: string, vars: Record<string, string | number>): string {
  return s.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

export default function ReferralPage() {
  const { user, token } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const smartBack = useSmartBack('/');
  const tr = (t.referral as any) ?? {};

  const [me, setMe] = useState<ReferralMe | null>(null);
  const [invited, setInvited] = useState<InvitedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const showToast = (m: string) => {
    setToast(m);
    setTimeout(() => setToast(null), 3000);
  };

  const fetchAll = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/referral/me`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/referral/list`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (r1.ok) {
        setMe(await r1.json());
      }
      if (r2.ok) {
        const d = await r2.json();
        setInvited(d.items ?? []);
      }
    } catch {
      setError(tr.loadError ?? 'Failed');
    } finally {
      setLoading(false);
    }
  }, [token, tr]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const onCopy = async () => {
    if (!me) return;
    try {
      await navigator.clipboard.writeText(me.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  const onShare = async () => {
    if (!me) return;
    const message = fmtTpl(tr.shareMessage ?? 'Join me! Code: {code}', { code: me.code });
    if (navigator.share) {
      try {
        await navigator.share({ title: tr.title ?? 'Invite', text: message });
        return;
      } catch {}
    }
    try {
      await navigator.clipboard.writeText(message);
      showToast(tr.copied);
    } catch {}
  };

  const onRedeem = async () => {
    if (!token || !redeemCode.trim()) return;
    setRedeemBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API}/referral/redeem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: redeemCode.trim() }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const errKey = `error_${data.detail}`;
        setError(tr[errKey] ?? tr.error_generic ?? 'Error');
        return;
      }
      if (data.balance != null) setCoinBalance(data.balance);
      showToast(fmtTpl(tr.redeemSuccess, { amount: data.coins_received }));
      setRedeemCode('');
      await fetchAll();
    } catch {
      setError(tr.error_generic ?? 'Error');
    } finally {
      setRedeemBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="ref-root">
        <div className="ref-container">
          <AppHeader onBack={smartBack} backLabel={t.common.back} />
          <div className="ref-empty">
            <h2>{tr.title ?? 'Referrals'}</h2>
            <p>{tr.loginRequired ?? 'Login required'}</p>
            <button className="ref-btn ref-btn--primary" onClick={() => navigate('/auth')}>
              {tr.goLogin ?? 'Sign in'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ref-root">
      <div className="ref-container">
      <AppHeader onBack={smartBack} backLabel={t.common.back} />

      <div className="ref-hero">
        <h1 className="ref-hero-title">🎁 {tr.title ?? 'Invite Friends'}</h1>
        <p className="ref-hero-sub">{tr.subtitle ?? ''}</p>
      </div>

      {loading && <div className="ref-loading">…</div>}

      {!loading && me && (
        <>
          <div className="ref-code-card">
            <div className="ref-code-label">{tr.yourCode ?? 'Your Code'}</div>
            <div className="ref-code-value">{me.code}</div>
            <div className="ref-code-actions">
              <button className="ref-btn ref-btn--ghost" onClick={onCopy}>
                {copied ? (tr.copied ?? 'Copied!') : (tr.copyCode ?? 'Copy')}
              </button>
              <button className="ref-btn ref-btn--primary" onClick={onShare}>
                📤 {tr.share ?? 'Share'}
              </button>
            </div>
          </div>

          <div className="ref-stats">
            <div className="ref-stat">
              <div className="ref-stat-num">{me.total_invited}</div>
              <div className="ref-stat-lbl">{tr.statInvited ?? 'Invited'}</div>
            </div>
            <div className="ref-stat">
              <div className="ref-stat-num">{me.total_active}</div>
              <div className="ref-stat-lbl">{tr.statActive ?? 'Active'}</div>
            </div>
            <div className="ref-stat">
              <div className="ref-stat-num">🪙 {me.total_earned}</div>
              <div className="ref-stat-lbl">{tr.statEarned ?? 'Earned'}</div>
            </div>
          </div>

          <div className="ref-section">
            <h2>{tr.howItWorks ?? 'How it works'}</h2>
            <ol className="ref-steps">
              <li>{tr.step1 ?? ''}</li>
              <li>{fmtTpl(tr.step2 ?? '', { signup: me.signup_bonus })}</li>
              <li>{fmtTpl(tr.step3 ?? '', { wins: me.activation_wins_required, bonus: me.active_bonus_referrer })}</li>
            </ol>
          </div>

          {!me.was_referred && (
            <div className="ref-section ref-redeem-card">
              <h2>{tr.redeemTitle ?? 'Redeem Code'}</h2>
              <p className="ref-redeem-desc">
                {fmtTpl(tr.redeemDesc ?? '', { amount: me.signup_bonus })}
              </p>
              <div className="ref-redeem-row">
                <input
                  className="ref-input"
                  type="text"
                  value={redeemCode}
                  onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                  placeholder={tr.redeemPlaceholder ?? 'Code'}
                  maxLength={16}
                />
                <button
                  className="ref-btn ref-btn--primary"
                  onClick={onRedeem}
                  disabled={redeemBusy || !redeemCode.trim()}
                >
                  {tr.redeemBtn ?? 'Redeem'}
                </button>
              </div>
              {error && <div className="ref-error">{error}</div>}
            </div>
          )}

          {me.was_referred && (
            <div className="ref-info-pill">✓ {tr.alreadyRedeemed ?? 'Already used a referral code'}</div>
          )}

          <div className="ref-section">
            <h2>{tr.invitedListTitle ?? 'Invited'}</h2>
            {invited.length === 0 ? (
              <div className="ref-empty-list">{tr.invitedEmpty ?? 'No one yet'}</div>
            ) : (
              <div className="ref-list">
                {invited.map((it) => (
                  <div key={it.id} className="ref-list-item">
                    <div className="ref-list-name">{it.nickname}</div>
                    <div className={`ref-list-status ref-list-status--${it.status}`}>
                      {it.status === 'active'
                        ? (tr.statusActive ?? 'Active')
                        : it.status === 'expired'
                        ? (tr.statusExpired ?? 'Expired')
                        : (tr.statusPending ?? 'Pending')}
                    </div>
                    <div className="ref-list-coins">🪙 {it.coins_earned}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {toast && <div className="ref-toast">{toast}</div>}
      </div>
    </div>
  );
}
